/**
 * Learning — temas de estudio con tarjetas generadas por Claude que se
 * empujan a Anki (y de ahí, vía AnkiWeb, a AnkiDroid en el celular).
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import {
  Plus, Trash2, Sparkles, Upload, RefreshCw, Check, X, BookOpen, AlertTriangle,
} from "lucide-react";

interface Topic {
  id: number;
  name: string;
  deck: string;
  notes: string;
  card_count: number;
  draft_count: number;
  pushed_count: number;
}

interface Card {
  id: number;
  topic_id: number;
  front: string;
  back: string;
  tags: string;
  status: "draft" | "approved" | "pushed" | "rejected";
  anki_note_id: number | null;
  source: string;
}

interface Status {
  installed: boolean;
  message?: string;
  error?: string;
  decks?: string[];
  stats?: { total: number; due: number; new: number };
}

const json = (r: Response) => r.json();

const post = (url: string, body?: unknown) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data?.error ?? `HTTP ${r.status}`);
    return data;
  });

const inputClass =
  "bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-sm font-mono px-3 py-2 w-full focus:border-[var(--bw-accent)] focus:outline-none";

export function LearningRoute() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newTopic, setNewTopic] = useState({ name: "", notes: "" });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [count, setCount] = useState(10);
  const [error, setError] = useState<string | null>(null);

  const { data: status } = useQuery<Status>({
    queryKey: ["learning", "status"],
    queryFn: () => fetch("/api/learning/status").then(json),
  });

  const { data: topics } = useQuery<Topic[]>({
    queryKey: ["learning", "topics"],
    queryFn: () => fetch("/api/learning/topics").then(json),
  });

  const selected = topics?.find((t) => t.id === selectedId) ?? null;

  const { data: cards } = useQuery<Card[]>({
    queryKey: ["learning", "cards", selectedId],
    queryFn: () => fetch(`/api/learning/topics/${selectedId}/cards`).then(json),
    enabled: selectedId !== null,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["learning"] });
  };

  const createTopic = useMutation({
    mutationFn: () => post("/api/learning/topics", newTopic),
    onSuccess: () => {
      setError(null);
      setShowCreate(false);
      setNewTopic({ name: "", notes: "" });
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteTopic = useMutation({
    mutationFn: (id: number) => fetch(`/api/learning/topics/${id}`, { method: "DELETE" }),
    onSuccess: () => { if (selectedId) setSelectedId(null); invalidate(); },
  });

  const saveNotes = useMutation({
    mutationFn: (notes: string) =>
      fetch(`/api/learning/topics/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      }).then(json),
    onSuccess: () => { setNotesDraft(null); invalidate(); },
  });

  const generate = useMutation({
    mutationFn: () =>
      post(`/api/learning/topics/${selectedId}/generate`, {
        notes: notesDraft ?? selected?.notes ?? "",
        count,
      }),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: Error) => setError(e.message),
  });

  const setCardStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      fetch(`/api/learning/cards/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      }).then(json),
    onSuccess: invalidate,
  });

  const approveAll = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(
        ids.map((id) =>
          fetch(`/api/learning/cards/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "approved" }),
          })
        )
      ),
    onSuccess: invalidate,
  });

  const deleteCard = useMutation({
    mutationFn: (id: number) => fetch(`/api/learning/cards/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const push = useMutation<{ pushed: number; duplicates: unknown[]; syncError: string | null }>({
    mutationFn: () => post(`/api/learning/topics/${selectedId}/push`),
    onSuccess: (r) => {
      setError(r.syncError ? `Tarjetas enviadas, pero el sync falló: ${r.syncError}` : null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const sync = useMutation({
    mutationFn: () => post("/api/learning/sync"),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: Error) => setError(e.message),
  });

  const drafts = cards?.filter((c) => c.status === "draft") ?? [];
  const approved = cards?.filter((c) => c.status === "approved") ?? [];
  const pushed = cards?.filter((c) => c.status === "pushed") ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <CRTHeader text="Learning" level={2} />
        <div className="flex gap-2">
          <GlyphButton
            variant="ghost"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            {sync.isPending ? "Sincronizando…" : "Sync"}
          </GlyphButton>
          <GlyphButton
            variant="secondary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setShowCreate(true)}
          >
            Nuevo tema
          </GlyphButton>
        </div>
      </div>

      {/* Estado de la integración */}
      {status && !status.installed && (
        <CorpoCard title="ANKI NO INSTALADO">
          <p className="text-sm text-[var(--bw-muted)]">
            {status.message} Hasta entonces puedes crear temas y generar tarjetas,
            pero no enviarlas al celular.
          </p>
        </CorpoCard>
      )}
      {status?.installed && status.stats && (
        <div className="flex gap-2 text-xs">
          <HexBadge variant="muted" size="sm">{status.stats.total} tarjetas</HexBadge>
          <HexBadge variant="muted" size="sm">{status.stats.due} por repasar</HexBadge>
          <HexBadge variant="muted" size="sm">{status.stats.new} nuevas</HexBadge>
        </div>
      )}

      {error && (
        <CorpoCard>
          <div className="flex items-start gap-2 text-sm text-[var(--bw-danger)]">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span className="font-mono">{error}</span>
          </div>
        </CorpoCard>
      )}

      {/* Nuevo tema */}
      {showCreate && (
        <CorpoCard title="NUEVO TEMA" accent>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">
                Nombre
              </label>
              <input
                value={newTopic.name}
                onChange={(e) => setNewTopic((t) => ({ ...t, name: e.target.value }))}
                placeholder="Ej: Bun runtime"
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">
                Material de origen (opcional)
              </label>
              <textarea
                value={newTopic.notes}
                onChange={(e) => setNewTopic((t) => ({ ...t, notes: e.target.value }))}
                placeholder="Pega apuntes, docs, fragmentos…"
                rows={4}
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <GlyphButton
              size="sm"
              onClick={() => createTopic.mutate()}
              disabled={!newTopic.name.trim() || createTopic.isPending}
            >
              Crear
            </GlyphButton>
            <GlyphButton variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
              Cancelar
            </GlyphButton>
          </div>
        </CorpoCard>
      )}

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        {/* Lista de temas */}
        <div className="space-y-2">
          {(topics ?? []).map((t) => (
            <button
              key={t.id}
              onClick={() => { setSelectedId(t.id); setNotesDraft(null); setError(null); }}
              className={`w-full text-left p-3 border transition-colors ${
                t.id === selectedId
                  ? "border-[var(--bw-accent)] bg-[var(--bw-surface-2)]"
                  : "border-[var(--bw-border)] hover:border-[var(--bw-accent)]"
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen size={14} className="text-[var(--bw-accent)] shrink-0" />
                <span className="text-sm font-bold text-[var(--bw-fg)] truncate">{t.name}</span>
              </div>
              <div className="flex gap-1 mt-1.5">
                {t.draft_count > 0 && (
                  <HexBadge variant="warning" size="sm">{t.draft_count} draft</HexBadge>
                )}
                <HexBadge variant="muted" size="sm">{t.pushed_count} en Anki</HexBadge>
              </div>
            </button>
          ))}
          {topics?.length === 0 && (
            <p className="text-xs text-[var(--bw-muted)] p-3">
              Sin temas todavía. Crea uno para empezar.
            </p>
          )}
        </div>

        {/* Detalle del tema */}
        {selected ? (
          <div className="space-y-4">
            <CorpoCard title={selected.name.toUpperCase()} accent>
              <p className="text-[10px] text-[var(--bw-muted)] font-mono mb-3">
                Mazo: {selected.deck}
              </p>

              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">
                Material de origen
              </label>
              <textarea
                value={notesDraft ?? selected.notes}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={6}
                placeholder="Pega aquí lo que quieras convertir en tarjetas…"
                className={inputClass}
              />

              <div className="flex flex-wrap gap-2 mt-3 items-center">
                {notesDraft !== null && notesDraft !== selected.notes && (
                  <GlyphButton
                    variant="ghost"
                    size="sm"
                    onClick={() => saveNotes.mutate(notesDraft)}
                  >
                    Guardar notas
                  </GlyphButton>
                )}
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={count}
                  onChange={(e) => setCount(parseInt(e.target.value) || 10)}
                  className={`${inputClass} w-20`}
                />
                <GlyphButton
                  size="sm"
                  icon={<Sparkles size={14} />}
                  onClick={() => generate.mutate()}
                  disabled={generate.isPending}
                >
                  {generate.isPending ? "Generando…" : "Generar tarjetas"}
                </GlyphButton>
                <GlyphButton
                  variant="secondary"
                  size="sm"
                  icon={<Upload size={14} />}
                  onClick={() => push.mutate()}
                  disabled={approved.length === 0 || push.isPending}
                >
                  {push.isPending ? "Enviando…" : `Enviar a Anki (${approved.length})`}
                </GlyphButton>
                <button
                  onClick={() => deleteTopic.mutate(selected.id)}
                  className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-danger)] transition-colors ml-auto"
                  title="Borrar tema (las tarjetas ya en Anki se quedan)"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </CorpoCard>

            {/* Borradores por revisar */}
            {drafts.length > 0 && (
              <CorpoCard title={`POR REVISAR (${drafts.length})`}>
                <div className="flex justify-end mb-2">
                  <GlyphButton
                    variant="ghost"
                    size="sm"
                    icon={<Check size={12} />}
                    onClick={() => approveAll.mutate(drafts.map((c) => c.id))}
                    disabled={approveAll.isPending}
                  >
                    {approveAll.isPending ? "Aprobando…" : "Aprobar todas"}
                  </GlyphButton>
                </div>
                <div className="space-y-2">
                  {drafts.map((c) => (
                    <div key={c.id} className="border border-[var(--bw-border)] p-3">
                      <p className="text-sm text-[var(--bw-fg)] font-bold">{c.front}</p>
                      <p className="text-sm text-[var(--bw-muted)] mt-1">{c.back}</p>
                      <div className="flex gap-2 mt-2">
                        <GlyphButton
                          size="sm"
                          icon={<Check size={12} />}
                          onClick={() =>
                            setCardStatus.mutate({ id: c.id, status: "approved" })
                          }
                        >
                          Aprobar
                        </GlyphButton>
                        <GlyphButton
                          variant="ghost"
                          size="sm"
                          icon={<X size={12} />}
                          onClick={() =>
                            setCardStatus.mutate({ id: c.id, status: "rejected" })
                          }
                        >
                          Descartar
                        </GlyphButton>
                      </div>
                    </div>
                  ))}
                </div>
              </CorpoCard>
            )}

            {/* Aprobadas, esperando push */}
            {approved.length > 0 && (
              <CorpoCard title={`APROBADAS (${approved.length})`}>
                <div className="space-y-1">
                  {approved.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 text-sm border-b border-[var(--bw-border)] py-1.5 last:border-0"
                    >
                      <span className="flex-1 text-[var(--bw-fg)] truncate">{c.front}</span>
                      <button
                        onClick={() => deleteCard.mutate(c.id)}
                        className="text-[var(--bw-muted)] hover:text-[var(--bw-danger)]"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </CorpoCard>
            )}

            {/* Ya en Anki */}
            {pushed.length > 0 && (
              <CorpoCard title={`EN ANKI (${pushed.length})`}>
                <div className="space-y-1">
                  {pushed.map((c) => (
                    <p
                      key={c.id}
                      className="text-sm text-[var(--bw-muted)] truncate border-b border-[var(--bw-border)] py-1.5 last:border-0"
                    >
                      {c.front}
                    </p>
                  ))}
                </div>
              </CorpoCard>
            )}
          </div>
        ) : (
          <CorpoCard>
            <p className="text-sm text-[var(--bw-muted)]">
              Selecciona un tema para generar y revisar tarjetas.
            </p>
          </CorpoCard>
        )}
      </div>
    </div>
  );
}
